import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faTrash as faTrashSolid } from '@fortawesome/pro-solid-svg-icons';
import { useMutation } from '@tanstack/react-query';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { ConfirmModal } from 'components/modal/ConfirmModal';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { sdClient } from 'contexts/SDKClientContext';
import { useUser } from 'contexts/UserContext';
import { NotificationSubscription } from 'custom_hooks/data_hooks/data_source/notificationSubscriptionsDataSource';
import { getNotificationTypeDisplayName } from 'pages/Settings/NotificationSubscriptions/shared';
import { useState } from 'react';
import { useThemeValues } from 'styles/theme';
import { capitalizeEachWord } from 'utils/string';

type NotificationSubscriptionDeleteButtonProps = {
    subscription: NotificationSubscription;
    onDelete: () => void;
};

export function NotificationSubscriptionDeleteButton({
    subscription,
    onDelete,
}: NotificationSubscriptionDeleteButtonProps) {
    const { user } = useUser();
    const { showToast } = useNotificationContext();
    const { secondaryText, deleteButton } = useThemeValues();
    const { canDeleteUserNotificationSubscription } = usePermissions();
    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

    const subscriptionType = getNotificationTypeDisplayName(subscription);

    const { mutate: deleteNotificationSubscription, isPending: isDeleting } = useMutation(
        {
            mutationFn: () =>
                sdClient.deleteNotificationSubscription({
                    subscriptionId: subscription.Id,
                }),

            onSuccess: () => {
                setConfirmationModalOpen(false);
                onDelete();
                showToast({
                    content: `Successfully deleted ${getNotificationTypeDisplayName(
                        subscription
                    )} notification subscription`,
                    type: 'success',
                });
            },

            onError: () => {
                showToast({
                    content: `Failed to delete ${getNotificationTypeDisplayName(
                        subscription
                    )} notification subscription`,
                    type: 'error',
                });
            },
        }
    );

    // Only Admins and Subscription creators can delete
    const canDelete =
        canDeleteUserNotificationSubscription ||
        subscription.CreatedBy?.UserId === user?.Id;

    return (
        <>
            <HoverIconButton
                title={
                    canDelete
                        ? `Delete ${getNotificationTypeDisplayName(
                              subscription
                          )} notification subscription`
                        : 'Cannot delete subscription created by an admin'
                }
                icon={faTrash}
                hoverIcon={faTrashSolid}
                color={secondaryText}
                hoverColor={deleteButton}
                onClick={() => setConfirmationModalOpen(true)}
                disabled={isDeleting || !canDelete}
            />
            {confirmationModalOpen && (
                <ConfirmModal
                    title={`Delete ${capitalizeEachWord(
                        subscriptionType
                    )} Notification Subscription`}
                    actionText={`delete this ${subscriptionType} notification subscription?`}
                    checkText={`delete this ${subscriptionType} notification subscription`}
                    actionButtonText="Delete"
                    closable={!isDeleting}
                    actionable={!isDeleting}
                    onAction={deleteNotificationSubscription}
                    onClose={() => setConfirmationModalOpen(false)}
                />
            )}
        </>
    );
}
